import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useQuery, gql, useLazyQuery } from '@apollo/client';
import { theme } from '../Theme';
import Loading from './Loading';
import Background from './Background';
import { getCookie } from '../utils/cookies';
import { logTrackAccessOnBlockchain } from '../utils/blockchain';

const GET_MUSIC_TRACKS_QUERY = gql`
  query getMusicTracks {
    getMusicTracks {
      id
      artist
      songName
    }
  }
`;

const DECRYPT_TRACK_QUERY = gql`
  query DecryptTrack($id: ID!) {
    decryptTrack(id: $id)
  }
`;

const HomeContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.textPrimary};
  position: relative;
  overflow: hidden;
`;

const PlayButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  padding: 10px 20px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.secondary};
  border: none;
  border-radius: 5px;
  cursor: pointer;
  z-index: 10;

  &:hover {
    background-color: ${theme.colors.accent};
  }
`;

const Home = () => {
  const { data: tracksData, loading: tracksLoading, error: tracksError } = useQuery(GET_MUSIC_TRACKS_QUERY);
  const [currentTrackUrl, setCurrentTrackUrl] = useState(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyzerRef = useRef(null);
  const sourceRef = useRef(null);

  const [decryptTrack, { loading: decryptLoading, error: decryptError }] = useLazyQuery(DECRYPT_TRACK_QUERY, {
    onCompleted: (data) => {
      if (data.decryptTrack) {
        const url = `data:audio/mpeg;base64,${data.decryptTrack}`;
        setCurrentTrackUrl(url);
      }
    },
    onError: (error) => {
      console.error('Error decrypting track:', error.message);
    }
  });

  useEffect(() => {
    if (tracksData && tracksData.getMusicTracks.length > 0) {
      const token = getCookie('token');
      const track = tracksData.getMusicTracks[0];
      setCurrentTrack(track);

      decryptTrack({
        variables: { id: track.id },
        context: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      });
    }
  }, [tracksData, decryptTrack]);

  const togglePlay = async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      if (audioRef.current && !sourceRef.current) {
        sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
        analyzerRef.current = audioContextRef.current.createAnalyser();
        analyzerRef.current.fftSize = 256;
        sourceRef.current.connect(analyzerRef.current);
        analyzerRef.current.connect(audioContextRef.current.destination);
      }
    }

    setIsPlaying((prev) => {
      if (!prev) {
        if (audioRef.current) {
          audioRef.current.play();
          logPlayEvent();
        }
      } else {
        if (audioRef.current) {
          audioRef.current.pause();
        }
      }
      return !prev;
    });
  };

  const logPlayEvent = async () => {
    try {
      if (currentTrack) {
        console.log('Logging play event: ', currentTrack);
        await logTrackAccessOnBlockchain(currentTrack.id);
        console.log(`Logged event: Played track: ${currentTrack.songName} by ${currentTrack.artist}`);
      }
    } catch (error) {
      console.error('Error logging play event:', error);
    }
  };

  if (tracksLoading || decryptLoading) return <Loading />;
  if (tracksError || decryptError) return <p>Error loading tracks</p>;

  return (
    <HomeContainer>
      <Background />
      <PlayButton onClick={togglePlay}>{isPlaying ? 'Stop' : 'Play'}</PlayButton>
      <audio id="audioPlayer" src={currentTrackUrl} ref={audioRef} controls style={{ display: 'none' }} />
    </HomeContainer>
  );
};

export default Home;